import React, { useContext, useState, useEffect, useRef } from 'react';
import { Box, Tab, Tabs, useMediaQuery, useTheme, Dialog, DialogTitle, DialogContent, Button, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import Unauthorized from "../../../layouts/Unauthorized";
import FormLogin from "../forms/FormLogin";
import FormSignup from "../forms/FormSignup";
import FormForgot from "../forms/FormForgot";
import FormConfirm from "../forms/FormConfirm";
import { ThemeContext } from "styled-components";
import { CsrfContext } from "../../../providers/CsrfProvider";
import SectionHeader from "../../template/components/SectionHeader";
import { signupUiEnabled } from "../../../config/auth";
import DevtoolsGuard from "../components/DevtoolsGuard";

const forgotTabIndex = signupUiEnabled ? 2 : 1;
const signupTabIndex = signupUiEnabled ? 1 : -1;

const ViewAuth: React.FC = () => {
    const [value, setValue] = useState<number>(0);
    const [forgotEmail, setForgotEmail] = useState<string>('');
    const theme = useContext(ThemeContext);
    const muiTheme = useTheme();
    const isSmallScreen = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const { csrfToken } = useContext(CsrfContext) as unknown as { csrfToken: string | null; setCsrfToken: (token: string) => void };
    const location = useLocation();
    const navigate = useNavigate();
    
    // Shared email state that persists across tab switches
    const [sharedEmail, setSharedEmail] = useState<string>(location.state?.prefillEmail || '');
    
    // Track if we've already cleaned up the initialTab from URL
    const hasCleanedUrl = useRef(false);

    // Check for initial tab from location state or URL params
    useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        const initialTab = location.state?.initialTab || urlParams.get('initialTab');

        if (initialTab === 'signup') {
            setValue(signupUiEnabled ? 1 : 0);
        } else if (initialTab === 'forgot') {
            setValue(forgotTabIndex);
        } else if (initialTab === 'login') {
            setValue(0); // Switch to login tab
        } else if (initialTab === 'confirm') {
            // For confirm flow, we'll show FormConfirm instead of tabs
            setValue(-1); // Use -1 to indicate confirm flow
        }

        // Remove initialTab from URL after processing (clean up URL)
        // Only do this once to avoid infinite loops
        if (!hasCleanedUrl.current && urlParams.has('initialTab')) {
            urlParams.delete('initialTab');
            const newSearch = urlParams.toString();
            // Use replace to avoid adding to history
            navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), {
                replace: true,
                state: location.state
            });
            hasCleanedUrl.current = true;
        }
    }, [location.state, location.search, location.pathname, navigate]);

    // Initialize shared email from location state if provided
    useEffect(() => {
        if (location.state?.prefillEmail && !sharedEmail) {
            setSharedEmail(location.state.prefillEmail);
        }
    }, [location.state?.prefillEmail]);

    const handleChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setValue(newValue);
        // No need to update URL - state is managed locally
    };

    // Show "logged out for inactivity" modal when redirected after session expiry
    const showLoggedOutModal = location.state?.logoutReason === 'session_expired';
    const closeLoggedOutModal = () => {
        navigate(location.pathname, { replace: true, state: {} });
    };

    return (
        <Unauthorized>
            <Dialog
                open={showLoggedOutModal}
                onClose={closeLoggedOutModal}
                aria-labelledby="logged-out-dialog-title"
                aria-describedby="logged-out-dialog-description"
                maxWidth={false}
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: theme?.colors?.backgroundPrimary ?? theme?.colors?.primary,
                        color: theme?.colors?.text,
                        borderRadius: '16px',
                        maxWidth: { xs: '95vw', sm: '600px' },
                        width: { xs: '95vw', sm: '70vw' },
                    }
                }}
            >
                <DialogTitle id="logged-out-dialog-title" sx={{ padding: 0 }}>
                    <SectionHeader
                        title="Session expired"
                        actionButton={undefined}
                        onActionClick={() => {}}
                        borderRadius="16px 16px 0 0"
                        margin={0}
                    />
                </DialogTitle>
                <DialogContent
                    id="logged-out-dialog-description"
                    sx={{
                        paddingTop: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                    }}
                >
                    <Box sx={{ px: 2, pt: '32px' }}>
                        <Typography variant="body1" sx={{ color: theme?.colors?.text }}>
                            You've been logged out due to inactivity. Please sign in again.
                        </Typography>
                    </Box>
                </DialogContent>
                <Box sx={{
                    paddingTop: 3,
                    paddingBottom: 3,
                    paddingLeft: 0,
                    paddingRight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1,
                }}>
                    <Box sx={{
                        display: 'flex',
                        gap: 2,
                        width: '100%',
                        px: 2,
                    }}>
                        <Button
                            onClick={closeLoggedOutModal}
                            fullWidth
                            variant="outlined"
                            sx={{
                                flex: 1,
                                borderRadius: '8px',
                                borderColor: theme?.current === 'light'
                                    ? theme?.colors?.brandSecondary
                                    : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                                color: '#ffffff',
                                backgroundColor: theme?.current === 'light'
                                    ? theme?.colors?.brandSecondary
                                    : theme?.colors?.background,
                                '&:hover': {
                                    borderColor: theme?.current === 'light'
                                        ? theme?.colors?.brandSecondary
                                        : (theme?.colors?.secondary || theme?.colors?.brandSecondary),
                                    backgroundColor: theme?.current === 'light'
                                        ? theme?.colors?.brandSecondary
                                        : (theme?.colors?.overlayHover || 'rgba(0, 0, 0, 0.04)'),
                                },
                            }}
                        >
                            Okay
                        </Button>
                    </Box>
                </Box>
            </Dialog>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'flex-start',
                minHeight: 'calc(var(--vh, 1vh) * 100)',
                width: '100%',
                maxWidth: '100%',
                boxSizing: 'border-box',
                backgroundColor: theme.colors.backgroundSecondary,
                padding: { xs: '20px 0', sm: '20px 0' }, // No side padding on mobile
                paddingTop: { xs: '80px', sm: '100px' }, // Less top padding on mobile
                marginTop: '0', // Ensure no margin conflicts
                overflowX: 'hidden', // Prevent horizontal scroll
                overflowY: 'auto'
            }}>
                <Box sx={{
                    position: 'relative',
                    width: { xs: '100%', sm: '400px' },
                    maxWidth: { xs: '100%', sm: '400px' },
                    minWidth: 0, // Allow container to shrink below content size
                    minHeight: 400,
                    boxShadow: 20,
                    borderRadius: { xs: 0, sm: 4 }, // No border radius on mobile to use full width
                    display: 'flex',
                    flexDirection: 'column',
                    backgroundColor: theme.colors.backgroundPrimary,
                    color: theme.colors.text,
                    padding: { xs: '16px', sm: '24px' }, // Add responsive padding
                    boxSizing: 'border-box',
                    overflow: 'hidden', // Prevent content overflow
                }}>
                    {value !== -1 && (
                        <Tabs 
                            value={value} 
                            onChange={handleChange} 
                            aria-label="login signup forgot tabs"
                            variant="scrollable" // Always scrollable - will trigger when tabs overflow
                            scrollButtons="auto"
                            allowScrollButtonsMobile
                            indicatorColor="primary" // Color of the tab indicator
                            textColor="inherit" // Color of the text in the tabs
                            sx={{
                                '.MuiTabs-flexContainer': {
                                    justifyContent: 'space-between', // Center and evenly space tabs
                                },
                                '.MuiTab-root': {
                                    flex: 1,  // Evenly spaced flexible cells
                                    minWidth: '100px', // Minimum width - triggers overflow when container is too narrow
                                    maxWidth: 'none',  // No max width constraint
                                    padding: '6px 12px', // Padding around text in each tab
                                    backgroundColor: 'transparent !important',
                                    transition: 'opacity 120ms ease, background-color 0ms !important',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    '&:hover, &:active, &:focus, &.Mui-focusVisible': {
                                        backgroundColor: 'transparent !important',
                                        transition: 'opacity 120ms ease, background-color 0ms !important',
                                    },
                                    '&.Mui-selected': {
                                        fontSize: '15px', // Font size when selected
                                        fontWeight: 'bold', // Optional: make text bold when selected
                                        backgroundColor: 'transparent !important',
                                    },
                                    '& .MuiTouchRipple-root': {
                                        display: 'none !important',
                                    },
                                    '& .MuiTouchRipple-ripple': {
                                        display: 'none !important',
                                    }
                                }
                            }}
                        >
                            <Tab label="Login" />
                            {signupUiEnabled && <Tab label="Join" />}
                            <Tab label="Forgot" />
                        </Tabs>
                    )}
                    <Box sx={{ display:'flex', flexGrow: 1, pt: 3 }}>
                        <DevtoolsGuard>
                        {value === -1 ? (
                            <FormConfirm />
                        ) : value === 0 ? (
                            <FormLogin prefillEmail={sharedEmail} onEmailChange={setSharedEmail} />
                        ) : value === signupTabIndex ? (
                            <FormSignup prefillEmail={sharedEmail} onEmailChange={setSharedEmail} />
                        ) : value === forgotTabIndex ? (
                            <FormForgot prefillEmail={sharedEmail} onEmailChange={setSharedEmail} />
                        ) : (
                            <FormLogin prefillEmail={sharedEmail} onEmailChange={setSharedEmail} />
                        )}
                        </DevtoolsGuard>
                    </Box>
                </Box>
            </Box>
        </Unauthorized>
    );
};

export default ViewAuth;
